import React, { useEffect, useState, forwardRef } from "react";
import { User, Loader2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { fileUrl, API_URL, api } from "@/api/client";
import { useI18n } from "@/i18n";
import { vhrpImages } from "@/lib/site";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const DigitalCard = forwardRef(function DigitalCard(
  { member, showQr = false },
  ref
) {
  const [photoUri, setPhotoUri] = useState(null);
  const { t, lang } = useI18n();
  const isIndic = lang !== "en";

  // Localized card VALUES in the chosen language (English shows originals).
  const [loc, setLoc] = useState(null);
  const [locLoading, setLocLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (lang === "en" || !member?.membership_id) {
      setLoc(null);
      return;
    }
    setLoc(null);
    setLocLoading(true);
    (async () => {
      try {
        const r = await api(`/membership/card/${lang}`);
        if (active) setLoc(r);
      } catch {
        if (active) setLoc(null);
      } finally {
        if (active) setLocLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [lang, member?.membership_id, member?.full_name, member?.father_name, member?.designation, member?.city, member?.state]);

  const show = (key, fallback) => (isIndic && loc && loc[key] ? loc[key] : fallback);

  useEffect(() => {
    let active = true;
    (async () => {
      if (member?.photo_path) {
        const u = await fileUrl(member.photo_path);
        if (active) setPhotoUri(u);
      } else {
        setPhotoUri(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [member?.photo_path]);

  if (!member) return null;

  const verifyUrl = member.membership_id
    ? `${API_URL}/verify/${member.membership_id}?format=html`
    : "";

  return (
    <div
      ref={ref}
      data-testid="digital-id-card"
      className="relative w-full overflow-hidden rounded-2xl"
      style={{ minHeight: 230, backgroundColor: "#29211D" }}
    >
      {/* Banner background */}
      <img
        src={vhrpImages.cardBg}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Gradient scrim */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(120deg, rgba(120,35,5,0.28) 0%, rgba(90,30,8,0.42) 55%, rgba(41,25,20,0.80) 100%)",
        }}
      />

      <div className="dc-content relative flex flex-col gap-4 p-5">
        {/* Header */}
        <div className="dc-header flex items-center gap-3">
          <div className="dc-logo flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-0 bg-white/95 shadow-md">
            <img src={vhrpImages.logoMark} alt="VHRP" className="dc-logo-img h-[78px] w-[78px] rounded-full object-cover" />
          </div>
          <div className="min-w-0 flex-1 flex flex-col justify-center">
            <div
              className="dc-org font-extrabold text-white"
              style={{
                fontSize: isIndic ? "1.5rem" : "1.125rem",
                lineHeight: 1.7,
                paddingTop: 3,
                paddingBottom: 2,
              }}
            >{t("orgName")}</div>
            <div className="dc-sub mt-0.5 text-xs font-bold uppercase tracking-widest text-white/80">
              {t("cardOfficialSub")}
            </div>
          </div>
          {showQr && verifyUrl && (
            <div className="dc-qr rounded-md bg-white p-1.5" data-testid="card-qr">
              <QRCodeCanvas value={verifyUrl} size={160} includeMargin={false} />
            </div>
          )}
        </div>

        {/* Body */}
        <div className="dc-body flex items-center gap-5">
          <div className="dc-photo h-28 w-24 shrink-0 overflow-hidden rounded-xl border-2 border-white/65 bg-black/20 shadow-md">
            {photoUri ? (
              <img src={photoUri} alt="" className="h-full w-full object-cover" crossOrigin="anonymous" />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <User size={48} color="rgba(255,255,255,0.7)" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="dc-name text-xl font-extrabold leading-snug text-white sm:text-2xl" style={{ overflowWrap: "anywhere" }}>
              {show("full_name", member.full_name) || "—"}
              {locLoading && !loc && <Loader2 className="ml-2 inline h-4 w-4 animate-spin text-white/70" />}
            </div>
            <div className="dc-desig mt-0.5 text-sm font-semibold leading-snug sm:text-base" style={{ color: "#FBE9E7" }}>
              {show("designation", member.designation) || t("defaultMember")}
            </div>
            <div className="dc-info mt-2 space-y-1">
              <div className="flex items-baseline gap-2 text-xs leading-normal">
                <span className="w-16 shrink-0 text-white/65">{t("cardSo")}</span>
                <span className="min-w-0 font-semibold text-white" style={{ overflowWrap: "anywhere" }}>{show("father_name", member.father_name) || "—"}</span>
              </div>
              <div className="flex items-baseline gap-2 text-xs leading-normal">
                <span className="w-16 shrink-0 text-white/65">{t("cardBlood")}</span>
                <span className="font-semibold text-white">{member.blood_group || "—"}</span>
              </div>
              <div className="dc-locrow flex flex-col gap-1 text-xs leading-normal sm:flex-row sm:items-baseline sm:gap-10">
                <div className="flex items-baseline gap-2">
                  <span className="w-16 shrink-0 text-white/65">{t("cardCity")}</span>
                  <span className="min-w-0 font-semibold text-white" style={{ overflowWrap: "anywhere" }}>{show("city", member.city) || "—"}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="dc-state-label w-16 shrink-0 text-white/65 sm:w-auto">{t("cardState")}</span>
                  <span className="min-w-0 font-semibold text-white" style={{ overflowWrap: "anywhere" }}>{show("state", member.state) || "—"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="dc-footer flex items-end justify-between border-t border-white/20 pt-3">
          <div>
            <div className="dc-foot-label text-[11px] font-bold uppercase tracking-widest text-white/65">
              {t("dMembershipId")}
            </div>
            <div className="dc-foot-val text-sm font-bold text-white">
              {member.membership_id || "PENDING"}
            </div>
          </div>
          <div className="text-right">
            <div className="dc-foot-label text-[11px] font-bold uppercase tracking-widest text-white/65">
              {t("cardValidUntil")}
            </div>
            <div className="dc-foot-val text-sm font-bold text-white">
              {formatDate(member.valid_until)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default DigitalCard;
